import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import { CircleDot } from "../../assets/icons/CircleDot";
import { LineDot } from "../../assets/icons/LineDot";
import { SquareDot } from "../../assets/icons/SquareDot";
import { StripeLine } from "../../assets/icons/StripeLine";
import { TriangleDot } from "../../assets/icons/TriangleDot";
import { getSourceLegends } from "../../utils/chartSourceUtils";
import {
  Container,
  PlaceholderDashedLine,
  StyledLegend,
} from "./CustomChartLegend.styles";

type MeasurementTypeName = {
  [key: string]: string;
};

// The dataKey is the identity; only the label the reader sees is translated (EN-5191).
const measurementTypeNames: MeasurementTypeName = {
  bp_diastolic: "chart_legend_diastolic",
  bp_systolic: "chart_legend_systolic",
};

export function CustomChartLegend(props: any) {
  const { t } = useTranslation();
  const {
    payload,
    focusMode,
    baseline,
    trendLine,
    ishltBaseline,
    chartConfig,
  } = props;

  const legend: any[] = [];

  const effectivePayload =
    payload && payload.length > 0
      ? payload
      : (chartConfig ?? []).map((cfg: any) => ({
          dataKey: cfg.yKey,
          payload: { name: cfg.source },
        }));

  if (baseline) {
    legend.push(
      <StyledLegend key="baseline-legend">
        <LineDot fill="#58C488" />
        {t("chart_legend_baseline")}
      </StyledLegend>
    );
  }

  if (ishltBaseline) {
    legend.push(
      <StyledLegend key="ishlt-baseline-legend">
        <LineDot fill="#754164" />
        {t("chart_legend_ishltBaseline")}
      </StyledLegend>
    );
  }

  if (trendLine) {
    legend.push(
      <StyledLegend key="Trendline">
        <StripeLine />
        {t("chart_legend_trendline")}
      </StyledLegend>
    );
  }

  effectivePayload.map((entry: any, index: any) => {
    const { dataKey, payload } = entry;
    const { name } = payload;
    const source = (name || "unknown").toLowerCase();

    if (source.includes("archived")) {
      return null;
    }

    const sourceLegend = getSourceLegends(dataKey, source) || {};
    const { shape, color, lineOpacity } = sourceLegend;

    const typeNameKey = measurementTypeNames[dataKey];
    const typeName = typeNameKey ? t(typeNameKey) : undefined;

    legend.push(
      <StyledLegend key={`${source}_${index}`}>
        {shape === "circle" && (
          <CircleDot
            fill={color}
            lineOpacity={lineOpacity}
            legend
            focusMode={focusMode}
          />
        )}
        {shape === "square" && <SquareDot fill={color} legend />}
        {shape === "triangle" && <TriangleDot fill={color} legend />}
        {shape === "line" && <LineDot fill={color} />}
        {typeName && `${typeName} - `}
        {sourceLegend.labelKey ? t(sourceLegend.labelKey) : sourceLegend.name}
      </StyledLegend>
    );

    return null;
  });

  if (legend.length === 0) {
    legend.push(
      <StyledLegend key="placeholder-circle" css={{ marginBottom: 4 }}>
        <CircleDot
          fill="#D69B82"
          lineOpacity={0.5}
          legend
          focusMode={focusMode}
        />
        <PlaceholderDashedLine />
      </StyledLegend>
    );
    legend.push(
      <StyledLegend key="placeholder-line">
        <LineDot fill="#9DB6E7" />
        <PlaceholderDashedLine />
      </StyledLegend>
    );
  }

  if (legend.length <= 4) {
    return <Container>{legend}</Container>;
  }

  return (
    <Tooltip
      title={legend.slice(4, legend.length)}
      zIndex={3002}
      color="white"
      placement="right"
      styles={{
        body: {
          height: "100%",
          width: "fit-content",
          background: "white !important",
          borderRadius: "4px !important",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
        },
      }}
    >
      <Container>{legend.slice(0, 4)}</Container>
    </Tooltip>
  );
}
